import { useQueryClient } from '@tanstack/react-query';
import { useModeAwareQuery, useModeAwareMutation } from './useModeAwareQuery';
import { useApi } from './useApi';

// ── API response / request shapes ────────────────────────────────────────────

export interface AgeingBandApi {
  id: string;
  name: string;
  startDay: number;
  endDay: number;
  color: string;
  alertEnabled: boolean;
  assignedCreditController: boolean;
  alertUserId?: string;
}

export interface MetricsSettingsResponse {
  dsoTarget: number;
  ageingBy: 'due-date' | 'transaction-date';
  showNotYetDueBand: boolean;
  bands: AgeingBandApi[];
}

export interface UpsertMetricsSettingsRequest {
  dsoTarget: number;
  ageingBy: 'due-date' | 'transaction-date';
  showNotYetDueBand: boolean;
  bands: AgeingBandApi[];
}

// ── Demo / mock data ─────────────────────────────────────────────────────────

const MOCK_METRICS_SETTINGS: MetricsSettingsResponse = {
  dsoTarget: 40,
  ageingBy: 'due-date',
  showNotYetDueBand: false,
  bands: [
    { id: '1', name: 'Current', startDay: 0, endDay: 30, color: '#5eead4', alertEnabled: false, assignedCreditController: false },
    { id: '2', name: '31-60 Days', startDay: 31, endDay: 60, color: '#2dd4bf', alertEnabled: false, assignedCreditController: false },
    { id: '3', name: '61-90 Days', startDay: 61, endDay: 90, color: '#14b8a6', alertEnabled: false, assignedCreditController: false },
    { id: '4', name: '91-120 Days', startDay: 91, endDay: 120, color: '#0d9488', alertEnabled: false, assignedCreditController: false },
    { id: '5', name: 'Over 120 Days', startDay: 121, endDay: 999, color: '#0f766e', alertEnabled: false, assignedCreditController: false },
  ],
};

// ── Query ────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch metrics settings (DSO target, ageing-by preference, and ageing bands).
 * Handles both Demo and Live modes.
 */
export function useMetricsSettings() {
  const { apiGet } = useApi();

  return useModeAwareQuery<MetricsSettingsResponse, Error>(
    ['metrics-settings'],
    async ({ signal }) => {
      return apiGet<MetricsSettingsResponse>(
        '/api/v1/settings/metrics',
        undefined,
        signal,
      );
    },
    MOCK_METRICS_SETTINGS,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}

// ── Mutation ─────────────────────────────────────────────────────────────────

/**
 * Hook to upsert metrics settings.
 * Handles both Demo and Live modes.
 */
export function useUpsertMetricsSettings() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<MetricsSettingsResponse, Error, UpsertMetricsSettingsRequest>(
    async (data) => {
      return apiPut<MetricsSettingsResponse>('/api/v1/settings/metrics', data);
    },
    async (data) => {
      return {
        dsoTarget: data.dsoTarget,
        ageingBy: data.ageingBy,
        showNotYetDueBand: data.showNotYetDueBand,
        bands: data.bands,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['metrics-settings'] });
      },
    },
  );
}
